import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OrganizationProjectService, AuthService, CharityChampionDraftResponse, MediaObject, DynamicTranslationService } from '@gs-2.0/core';
import { MediaUploadValue } from '../media-upload-control/media-upload-control.component';
import { ToastService } from '../../services/toast.service';

export interface BeAChampionDialogData {
  charityName: string;
  charityUuid: string;
  organisationChampionLinkUuid?: string;
}

interface MonthOption {
  value: string;
  label: string;
}

interface DurationOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-be-a-champion-dialog',
  standalone: false,
  templateUrl: './be-a-champion-dialog.component.html',
  styleUrls: ['./be-a-champion-dialog.component.scss']
})
export class BeAChampionDialogComponent implements OnInit {
  championForm!: FormGroup;
  isSavingDraft: boolean = false;
  isSubmitting: boolean = false;
  isMediaUploading: boolean = false;

  months: MonthOption[] = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  years: string[] = [];

  durations: DurationOption[] = [
    { value: '3', label: '3 months' },
    { value: '6', label: '6 months' },
    { value: '9', label: '9 months' },
    { value: '12', label: '12 months' }
  ];

  constructor(
    public dialogRef: MatDialogRef<BeAChampionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BeAChampionDialogData,
    private fb: FormBuilder,
    private organizationProjectService: OrganizationProjectService,
    private authService: AuthService,
    private toastService: ToastService,
    private translationService: DynamicTranslationService
  ) {
    // Generate years from current year to 50 years ago
    const currentYear = new Date().getFullYear();
    for (let i = 0; i <= 50; i++) {
      this.years.push((currentYear - i).toString());
    }
  }

  ngOnInit(): void {
    this.championForm = this.fb.group({
      engagementMonth: ['6', Validators.required],
      engagementYear: [new Date().getFullYear().toString(), Validators.required],
      testimonial: ['', Validators.required],
      duration: ['12', Validators.required],
      mediaUpload: [null]
    });

    // Load draft data if available
    const userUuid = this.authService.userUuid;

    if (userUuid && this.data.charityUuid) {
      this.organizationProjectService.getCharityChampionDraft(
        this.data.charityUuid,
        userUuid
      ).subscribe({
        next: (response: CharityChampionDraftResponse) => {
          if (response?.response?.championLinkedEntityData) {
            const draftData = response.response.championLinkedEntityData;

            // Populate form with draft data
            this.championForm.patchValue({
              engagementMonth: draftData.engagementMonth?.toString() || '6',
              engagementYear: draftData.engagementYear?.toString() || new Date().getFullYear().toString(),
              testimonial: draftData.testimonial || '',
              duration: draftData.validityMonth?.toString() || '12'
            });

            // Load media (photo or video) if available in draft
            let mediaObject: MediaObject | null = null;

            if (draftData.photoOrVideo) {
              mediaObject = draftData.photoOrVideo;
            } else if (draftData.photo) {
              mediaObject = draftData.photo;
            } else if (draftData.video) {
              mediaObject = draftData.video;
            }

            if (mediaObject && typeof mediaObject === 'object' && mediaObject.uuid) {
              const fileName = mediaObject.filename ?
                mediaObject.filename.split('/').pop() || 'Uploaded File' :
                'Uploaded File';

              const mediaValue: MediaUploadValue = {
                uuid: mediaObject.uuid,
                url: mediaObject.url,
                filename: fileName,
                type: mediaObject.type
              };

              this.championForm.patchValue({
                mediaUpload: mediaValue
              });

              console.log('Loaded media from draft:', mediaValue);
            }

            // Update organisationChampionLinkUuid if it exists in the response
            if (response.response.organisationChampionLinkUuid) {
              this.data.organisationChampionLinkUuid = response.response.organisationChampionLinkUuid;
            }
          }
        },
        error: (error) => {
          console.error('Error loading draft:', error);
        }
      });
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onMediaUploadStateChange(isUploading: boolean): void {
    this.isMediaUploading = isUploading;
  }

  onSaveAsDraft(): void {
    // Allow saving draft even with invalid form
    const formValue = this.championForm.value;

    // Prepare the champion data structure
    const championData = {
      organizationEngagement: {
        engagementStartMonth: { uuid: formValue.engagementMonth },
        engagementStartYear: { uuid: formValue.engagementYear }
      },
      photoOrVideo: {
        uuid: formValue.mediaUpload?.uuid || null
      },
      testimonial: formValue.testimonial || '',
      testimonialVisibility: { uuid: formValue.duration }
    };

    this.isSavingDraft = true;

    this.organizationProjectService.saveChampionCharityDraft(
      this.data.charityUuid,
      this.data.organisationChampionLinkUuid || '',
      championData
    ).subscribe({
      next: (response) => {
        this.isSavingDraft = false;
        const successMessage = this.translationService.instant('champion.dialog.toast.draft.success');
        this.toastService.success(successMessage);
        this.dialogRef.close({ action: 'draft', success: true, data: response });
      },
      error: (error) => {
        this.isSavingDraft = false;
        console.error('Error saving draft:', error);
        const errorMessage = this.translationService.instant('champion.dialog.toast.error');
        this.toastService.error(errorMessage);
        this.dialogRef.close({ action: 'draft', success: false, error });
      }
    });
  }

  onSubmit(): void {
    if (this.championForm.valid) {
      const formValue = this.championForm.value;

      // Prepare the champion data structure
      const championData = {
        organizationEngagement: {
          engagementStartMonth: { uuid: formValue.engagementMonth },
          engagementStartYear: { uuid: formValue.engagementYear }
        },
        photoOrVideo: {
          uuid: formValue.mediaUpload?.uuid || null
        },
        testimonial: formValue.testimonial,
        testimonialVisibility: { uuid: formValue.duration }
      };

      this.isSubmitting = true;

      this.organizationProjectService.championCharity(
        this.data.charityUuid,
        this.data.organisationChampionLinkUuid || '',
        championData
      ).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          const successMessage = this.translationService.instant('champion.dialog.toast.submit.success');
          this.toastService.success(successMessage);
          this.dialogRef.close({ action: 'submit', success: true, data: response });
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error submitting champion form:', error);
          const errorMessage = this.translationService.instant('champion.dialog.toast.error');
          this.toastService.error(errorMessage);
          this.dialogRef.close({ action: 'submit', success: false, error });
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.championForm.controls).forEach(key => {
        this.championForm.get(key)?.markAsTouched();
      });
    }
  }
}
